import {Component, EventEmitter, Inject, Input, Output, PLATFORM_ID} from '@angular/core';
import {ChatHttpService} from "../../../../services/http-services/chat/chat-http.service";
import {ProfileHttpService} from "../../../../services/http-services/profile/profile-http.service";
import {AuthenticationHelperService} from "../../../../services/helpers/authentication-helper.service";
import {FormsModule} from "@angular/forms";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {Subject} from "rxjs";
import {isPlatformBrowser, NgClass, NgForOf, NgIf} from "@angular/common";
import { ToastrService } from 'ngx-toastr';
import {TranslateModule} from "@ngx-translate/core";
import {UserRoles} from "../../../../enums/general-enums";
import {CheckActiveRoleModule} from '../../../../pipes/check-active-role/check-active-role.module';

@Component({
  selector: 'tl-sharing-options-modal',
  standalone: true,
  imports: [
    FormsModule,
    NgIf,
    NgForOf,
    TranslateModule,
    NgClass,
    CheckActiveRoleModule
  ],
  templateUrl: './sharing-options-modal.component.html',
})
export class SharingOptionsModalComponent {
  @Input() urlForShare: string = '';
  @Input() entityType = '';
  @Input() entityId!: number;
  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();
  searchTerm$ = new Subject<string>();
  users: any[] = [];
  groups: any[] = [];
  linkCopied = false;
  hasSelectedUsers = false;
  urlParams = {search_keyword: '', is_share: 1};
  socialApps = [
    { name: 'Facebook', icon: '/assets/images/social-media/social-media-fb.png' },
    { name: 'WhatsApp', icon: '/assets/images/social-media/social-media-whats-app.png' },
    { name: 'Viber', icon: '/assets/images/social-media/social-media-viber.png' },
    { name: 'X', icon: '/assets/images/social-media/social-media-x.png' },
    { name: 'Snapchat', icon: '/assets/images/social-media/social-media-snapchat.png' },
  ];
  protected readonly UserRoles = UserRoles;

  constructor(private profileHttpService: ProfileHttpService,
              private chatHttpService: ChatHttpService,
              @Inject(PLATFORM_ID)
              private platformId: string) {
  }

  ngOnInit(): void {
    this.searchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe((searchTerm: any) => {
      this.urlParams.search_keyword = searchTerm;
      this.getUsers();
    });
    this.getUsers();
  }

  getUsers(): void {
    this.profileHttpService.getUserForShare(this.urlParams).then(res => {
      this.users = res.data.users;
      this.groups = res.data.groups;
    });
  }

  onSearchChange(event: any): void {
    this.searchTerm$.next(event.target.value);
  }

  clearSearch(): void {
    this.searchTerm$.next('');
  }

  onSelectUserChange(): void {
    this.hasSelectedUsers = this.users.some((user: any) => user.selected) || this.groups.some((group: any) => group.selected);
  }

  shareInternal(): void {
    const participants = this.users
      .filter(user => user.selected)
      .map(user => user.id);
    const conversations = this.groups
      .filter(group => group.selected)
      .map(group => group.id);
    this.chatHttpService.postShareToConversationsV2({type: this.entityType, entity_id: this.entityId, participants, conversations});
    this.closeModal.emit();
  }

  async shareExternal(platform: string): Promise<void> {
    let shareUrl = '';
    const encodedUrl = encodeURIComponent(this.urlForShare);
    switch (platform.toLowerCase()) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodedUrl}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'viber':
        shareUrl = `viber://forward?text=${encodedUrl}`;
        break;
      case 'x':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}`;
        break;
      case 'snapchat':
        shareUrl = `https://www.snapchat.com/share?url=${encodedUrl}`;
        break;
      default:
        return;
    }
    if (isPlatformBrowser(this.platformId)) {
      window.open(shareUrl, '_blank');
    }
    setTimeout(() => {
      this.closeModal.emit();
    });
  }

  async shareExternalWithShareBtnOptions(): Promise<void> {
    if (navigator.share) {
      navigator.share({
        title: 'Share',
        url: this.urlForShare,
      });
    } else {
      alert('Web Share API is not supported in your browser.');
    }
    setTimeout(() => {
      this.closeModal.emit();
    });
  }

  async copyLink(): Promise<void> {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(this.urlForShare);
      this.linkCopied = true;
      setTimeout(() => this.linkCopied = false, 2000);
    } else {
      alert('Clipboard API is not supported in your browser.');
    }
  }
}
